<template>
  <h1 class="text-center text-base mb-3 font-semibold">Create Section</h1>
  <div class="space-y-3">
    <div v-for="(section, index) in sections" :key="index"
      class="p-2.5 border border-gray-200 rounded-lg space-y-2 bg-gray-50">
      <!-- Header with Delete Icon -->
      <div class="flex justify-between items-center mb-1">
        <h3 class="font-semibold text-gray-700 text-sm">Section {{ index + 1 }}</h3>
        <i @click="removeSection(index)" class="pi pi-trash text-red-500 cursor-pointer hover:text-red-700 text-sm"
          v-if="sections.length > 1"></i>
      </div>
      <div class="flex flex-col gap-2">
        <!-- Section Name -->
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-medium text-gray-700">Section (Arm) Name</label>
          <InputText v-model="section.name" placeholder="Enter section name" class="w-full"
            :class="{ 'p-invalid': getErrorMessage('name') }" />
          <small v-if="getErrorMessage('name')" class="text-red-500">{{ getErrorMessage('name') }}</small>
        </div>

        <!-- Classroom MultiSelect -->
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-medium text-gray-700">Select Classrooms</label>
          <MultiSelect v-model="section.classroom_ids" :options="classrooms" optionLabel="name" optionValue="id"
            placeholder="Select classrooms" display="chip" class="w-full" filter
            :class="{ 'p-invalid': getErrorMessage('classroom_ids') }" />
          <small v-if="getErrorMessage('classroom_ids')" class="text-red-500">{{ getErrorMessage('classroom_ids')
          }}</small>
        </div>
      </div>
    </div>

    <div class="flex gap-1.5 justify-end mt-2">
      <Button label="Add Another Section" icon="pi pi-plus" size="small" class="p-button-outlined" @click="addSection"
        :disabled="isSubmitting" />
      <Button label="Submit all sections" size="small" @click="submitAllSections" :loading="isSubmitting"
        class="!bg-primary !border-primary" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useUserStore } from '@/store/userStore';
import { useToast } from 'primevue/usetoast';
import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';
import { useFormErrors } from '@/composables/useFormErrors';
import { useSchoolMutations } from '@/composables/mutations/school/useSchoolMutations';

import { extractGraphQLError } from '@/utils/graphqlError';
const toast = useToast();
const userStore = useUserStore();
const { serverErrors, handleServerErrors, clearErrors, getErrorMessage } = useFormErrors();
const { createSection } = useSchoolMutations();

const { classrooms, refetchSchool } = useSchoolQuery();

const emit = defineEmits(['success']);
const isSubmitting = ref(false);

const sections = ref([{ name: '', classroom_ids: [] as string[] }]);

const addSection = () => {
  sections.value.push({ name: '', classroom_ids: [] });
};

const removeSection = (index: number) => {
  if (sections.value.length > 1) {
    sections.value.splice(index, 1);
  } else {
    toast.add({ severity: 'warn', summary: 'Warning', detail: 'You must have at least one section.', life: 3000 });
  }
};

const submitAllSections = async() => {
  isSubmitting.value = true;
  clearErrors();
  try {
    for (let i = 0; i < sections.value.length; i++) {
      const section = sections.value[i];
      await createSection({
        name: section?.name.trim(),
        classroom_ids: section?.classroom_ids.map(String),
      });
    }
    emit('success');

  } catch (err) {
    handleServerErrors(err);
    if (Object.keys(serverErrors.value).length === 0) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(err, 'Error submitting sections.'), life: 3000 });
    }
  } finally {
    isSubmitting.value = false;
  }
};

// Watch for modal visibility and refetch classrooms
// Note: In the original code this watched dropdownOpenIndex which is removed.
// We can just refetch on mount or rely on autoFetch.
// Since this component is mounted when step becomes active, refetchSchool is called on init of useSchoolQuery.
</script>
